import React, { useEffect, useState } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Button,
  Grid,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import useAddTask from '../../../hooks/taskHooks/useAddTask'
import useUpdateTask from '../../../hooks/taskHooks/useUpdateTask'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'

import showToast from '../../Common/SweetAlert/ToastAlert'
import useConfirmationAlert from '../../../hooks/componentHooks/useConfirmationAlert'

import { validateDate, validateString, validateURL } from '../../../utils/validationUtils'
import LoadingBackdrop from '../../Common/Loading/LoadingBackdrop'
import { useLoading } from '../../../context/LoadingContext'

interface TaskFormProps {
  action: string
  onSave: () => void
  taskData?: any // Define the appropriate type if possible
  onFormChange: (hasChanges: boolean) => void
}

const TaskForm: React.FC<TaskFormProps> = ({ action, onSave, taskData: taskData, onFormChange }) => {
  useSelector((state: RootState) => state.task)
  const { loading } = useLoading()

  const { addTask } = useAddTask()
  const { handleUpdateTask } = useUpdateTask()
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  const [hasChanges, setHasChanges] = useState(false)
  const [initialValues] = useState({
    id: action !== 'createTask' ? taskData.id : null,
    title: action !== 'createTask' ? taskData.title : '',
    details: action !== 'createTask' ? taskData.details : '',
    dueDate: action !== 'createTask' ? new Date(taskData.dueDate) : (null as Date | null),
    link: action !== 'createTask' ? taskData.link : '',
    importance: action !== 'createTask' ? taskData.importance : '',
    status: action !== 'createTask' ? taskData.status : 'Not Started',
    createdBy: action !== 'createTask' ? taskData.createdBy : '',
  })

  const [formData, setFormData] = useState(initialValues)
  const [validationErrors, setValidationErrors] = useState({
    title: '',
    details: '',
    dueDate: '',
    link: '',
    importance: '',
  })

  const handleDateChange = (newValue: Date | null) => {
    setFormData((prevData) => ({
      ...prevData,
      dueDate: newValue,
    }))

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      dueDate: '',
    }))
  }

  useEffect(() => {
    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialValues)
    setHasChanges(formChanged)
    onFormChange(formChanged)
  }, [formData, initialValues, onFormChange])

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>,
  ) => {
    const { name, value } = event.target
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }))
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    validateForm({ name, value })
  }

  const handleClear = () => {
    setFormData(initialValues)
  }

  const validateForm = ({ name, value }: { name?: string; value?: any } = {}) => {
    const errors: any = {}

    if (name) {
      let error = ''
      switch (name) {
        case 'title':
          error = validateString(value, 'Task title', { required: true })
          break
        case 'details':
          error = validateString(value, 'Task details', { required: true })
          break
        case 'dueDate':
          error = validateDate(value, 'Task due date', { required: true })
          break
        case 'link':
          error = validateURL(value, 'Task link', { required: false })
          break
        case 'importance':
          error = validateString(value, 'Task importance', { required: true })
          break
        default:
          break
      }
      setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: error }))
      return error
    }

    Object.entries(formData).forEach(([name, value]) => {
      let error = ''
      switch (name) {
        case 'title':
          error = validateString(value, 'Task title', { required: true })
          if (error !== '') errors.title = error
          break
        case 'details':
          error = validateString(value, 'Task details', { required: true })
          if (error !== '') errors.details = error
          break
        case 'dueDate':
          error = validateDate(value, 'Task due date', { required: true })
          if (error !== '') errors.dueDate = error
          break
        case 'link':
          error = validateURL(value, 'Task link', { required: false })
          if (error !== '') errors.link = error
          break
        case 'importance':
          error = validateString(value, 'Task importance', { required: true })
          if (error !== '') errors.importance = error
          break
        default:
          break
      }
    })

    setValidationErrors(errors)

    return Object.values(errors).length > 0
  }

  const handleTaskOperation = async () => {
    const hasError = validateForm()
    if (!hasError) {
      openConfirmationAlert({
        title: action !== 'createTask' ? 'Update Task' : 'Create New Task',
        text:
          action !== 'createTask'
            ? 'Are you sure you want to update this task?'
            : 'Are you sure you want to create new task?',
        onConfirm: async () => {
          if (action === 'createTask') {
            await addTask(formData)
            showToast('success', 'Task created successfully!')
            handleClear()
          } else {
            handleUpdateTask(formData)
            showToast('success', 'Task updated successfully!')
          }
          onSave()
        },
      })
    }
  }

  return (
    <Box py={3} px={5}>
      <Grid container direction='column' spacing={2} alignItems='center' size={12}>
        <Grid container spacing={2} alignItems='center' display='flex' size={12}>
          <Grid container spacing={2} alignItems='stretch' display='flex' direction='column' size={12}>
            <Grid>
              <TextField
                id='task-title-input'
                label='Task Title'
                variant='outlined'
                fullWidth
                name='title'
                value={formData.title}
                onChange={handleFormChange}
                onBlur={handleBlur}
                helperText={validationErrors.title}
                error={!!validationErrors.title}
                required
              />
            </Grid>
            <Grid>
              <TextField
                id='task-details-input'
                label='Task Details'
                variant='outlined'
                multiline
                rows={4}
                fullWidth
                name='details'
                value={formData.details}
                onChange={handleFormChange}
                onBlur={handleBlur}
                helperText={validationErrors.details}
                error={!!validationErrors.details}
                required
              />
            </Grid>
            <Grid>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label='Due Date'
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                      helperText: validationErrors.dueDate,
                      error: !!validationErrors.dueDate,
                      id: 'due-date-input',
                    },
                    field: { clearable: true },
                    actionBar: {
                      actions: ['today', 'clear', 'cancel', 'accept'],
                    },
                  }}
                  name='dueDate'
                  value={formData.dueDate}
                  onChange={handleDateChange}
                  disablePast
                />
              </LocalizationProvider>
            </Grid>
            <Grid>
              <TextField
                fullWidth
                id='task-link-input'
                label='Link'
                value={formData.link}
                onChange={handleFormChange}
                name='link'
                onBlur={handleBlur}
                helperText={validationErrors.link}
                error={!!validationErrors.link}
              />
            </Grid>
            <Grid>
              <FormControl fullWidth required error={!!validationErrors.importance}>
                <InputLabel id='task-importance-input' error={!!validationErrors.importance}>
                  Importance
                </InputLabel>
                <Select
                  labelId='task-importance'
                  value={formData.importance}
                  label='Importance'
                  onChange={handleFormChange}
                  onBlur={handleBlur}
                  error={!!validationErrors.importance}
                  name='importance'
                >
                  <MenuItem value='Optional' id='optional_select'>
                    Optional
                  </MenuItem>
                  <MenuItem value='Required' id='required_select'>
                    Required
                  </MenuItem>
                </Select>
                <FormHelperText>{validationErrors.importance}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} justifyContent={'flex-end'} direction='row' alignItems='center' mt={3}>
        <Grid>
          {action !== 'updateTask' && (
            <Button
              id='clear-event-form-button'
              color='info'
              onClick={handleClear}
              disabled={!hasChanges}
            >
              Clear
            </Button>
          )}
        </Grid>
        <Grid>
          <Button
            onClick={handleTaskOperation}
            id={`${action === 'updateTask' ? 'update' : 'save'}-event-form-button`}
            variant='contained'
            color={action === 'updateTask' ? 'primary' : 'success'}
          >
            {action === 'updateTask' ? 'Update' : 'Create'}
          </Button>
        </Grid>
      </Grid>
      {loading && (
        <LoadingBackdrop message={action === 'updateTask' ? 'Updating task...' : 'Creating task...'} />
      )}
      {ConfirmationAlert}
    </Box>
  )
}

export default TaskForm
